import {SelectionModel} from '@angular/cdk/collections';
import {AfterViewInit, Component, OnInit, ViewChild} from '@angular/core';
import {ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup} from '@angular/forms';
import {MatButton, MatIconButton} from '@angular/material/button';
import {MatCheckbox} from '@angular/material/checkbox';
import {MatDialog} from '@angular/material/dialog';
import {MatDivider} from '@angular/material/divider';
import {MatFormField, MatLabel, MatSuffix} from '@angular/material/form-field';
import {MatIcon} from '@angular/material/icon';
import {MatInput} from '@angular/material/input';
import {MatMenu, MatMenuItem, MatMenuTrigger} from '@angular/material/menu';
import {MatPaginator} from '@angular/material/paginator';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {MatSnackBar} from '@angular/material/snack-bar';
import {MatSort, MatSortHeader} from '@angular/material/sort';
import {
    MatCell,
    MatCellDef,
    MatColumnDef,
    MatHeaderCell,
    MatHeaderCellDef,
    MatHeaderRow,
    MatHeaderRowDef,
    MatRow,
    MatRowDef,
    MatTable,
    MatTableDataSource,
} from '@angular/material/table';

import {EditTagDialogComponent} from '@app/admin/edit-tag-dialog/edit-tag-dialog.component';
import {MergeTagDialogComponent} from '@app/admin/merge-tag-dialog/merge-tag-dialog.component';
import {ConfirmDialogComponent} from '@app/shared/confirm-dialog/confirm-dialog.component';
import {ManagedComponent} from '@app/shared/managed.component';
import {Tag} from '@app/tag/models';
import {TagService} from '@app/tag/tag.service';
import {merge, of as observableOf} from 'rxjs';
import {catchError, debounceTime, map, startWith, switchMap} from 'rxjs/operators';

@Component({
    selector: 'frog-tag-admin',
    templateUrl: './tag-admin.component.html',
    styleUrls: ['./tag-admin.component.scss'],
    imports: [
        MatButton,
        MatMenuTrigger,
        MatIcon,
        MatMenu,
        MatMenuItem,
        ReactiveFormsModule,
        MatFormField,
        MatLabel,
        MatInput,
        MatIconButton,
        MatSuffix,
        MatCheckbox,
        MatProgressSpinner,
        MatTable,
        MatSort,
        MatColumnDef,
        MatHeaderCellDef,
        MatHeaderCell,
        MatCellDef,
        MatCell,
        MatSortHeader,
        MatDivider,
        MatHeaderRowDef,
        MatHeaderRow,
        MatRowDef,
        MatRow,
        MatPaginator,
    ],
})
export class TagAdminComponent extends ManagedComponent implements OnInit, AfterViewInit {
    public displayedColumns: string[];
    public dataSource: MatTableDataSource<Tag>;
    public resultsLength;
    public isLoadingResults;
    public selection: SelectionModel<Tag>;
    public form: UntypedFormGroup;
    public error: string;

    @ViewChild(MatPaginator) paginator: MatPaginator;
    @ViewChild(MatSort) sort: MatSort;

    constructor(
        private tagService: TagService,
        private matDialog: MatDialog,
        private snackBar: MatSnackBar,
        private formBuilder: UntypedFormBuilder,
    ) {
        super();

        this.displayedColumns = ['select', 'name', 'category', 'count', 'actions'];
        this.dataSource = new MatTableDataSource<Tag>();
        this.selection = new SelectionModel<Tag>(true, []);
        this.resultsLength = 0;
        this.isLoadingResults = true;
        this.error = null;

        this.form = this.formBuilder.group({
            unusedOnly: false,
            query: '',
        });
    }

    ngOnInit(): void {}

    ngAfterViewInit() {
        // If the user changes the sort order, reset back to the first page.
        this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0));

        const sub = merge(
            this.form.valueChanges.pipe(debounceTime(300)),
            this.sort.sortChange,
            this.paginator.page,
        )
            .pipe(
                startWith({}),
                switchMap(() => {
                    this.isLoadingResults = true;
                    this.error = null;
                    // Combine direction and column, e.g. -name, count, etc.
                    const ordering = (this.sort.direction === 'asc' ? '' : '-') + this.sort.active;
                    return this.tagService.list(
                        this.paginator.pageIndex,
                        this.form.value.query,
                        [],
                        this.form.value.unusedOnly,
                        ordering,
                    );
                }),
                map((data) => {
                    // Flip flag to show that loading has finished.
                    this.isLoadingResults = false;
                    this.resultsLength = data.count;

                    return data.results;
                }),
                catchError((err) => {
                    this.isLoadingResults = false;
                    this.error = err.toString();
                    return observableOf([]);
                }),
            )
            .subscribe((data) => {
                this.selection.clear();
                this.dataSource.data = data;
            });
        this.subscriptions.push(sub);
    }

    isAllSelected(): boolean {
        return this.selection.selected.length === this.dataSource.data.length;
    }

    masterToggle(): void {
        this.isAllSelected()
            ? this.selection.clear()
            : this.dataSource.data.forEach((row) => this.selection.select(row));
    }

    clearFilter() {
        this.form.patchValue({
            query: '',
        });
    }

    removeTags(tags: Tag[]) {
        const dialog = this.matDialog.open(ConfirmDialogComponent, {
            data: {
                title: 'Remove Tags',
                message: `Are you sure you want to remove ${tags.length} tag(s)?`,
                acceptLabel: 'Yes',
                rejectLabel: 'No',
            },
        });
        dialog.afterClosed().subscribe((result) => {
            if (result) {
                this.selection.clear();
                this.tagService.delete(tags).subscribe(
                    () => {
                        const data = this.dataSource.data;
                        tags.forEach((tag) => {
                            for (let i = data.length - 1; i >= 0; i--) {
                                if (data[i].id === tag.id) {
                                    data.splice(i, 1);
                                    break;
                                }
                            }
                        });
                        this.dataSource.data = data;
                    },
                    (err) => {
                        this.snackBar.open(err.error.detail, null, {
                            duration: 2000,
                            panelClass: ['error-snackbar'],
                        });
                    },
                );
            }
        });
    }

    editTag(tag: Tag) {
        this.matDialog
            .open(EditTagDialogComponent, {
                data: tag,
            })
            .afterClosed()
            .subscribe((name) => {
                if (name) {
                    const updatedTag = Object.assign(new Tag(), tag);
                    updatedTag.name = name;
                    this.selection.clear();
                    this.tagService.update(updatedTag).subscribe(
                        () => {
                            tag.displayName = name;
                        },
                        (err) => {
                            this.snackBar.open(err.error.detail, null, {
                                duration: 2000,
                                panelClass: ['error-snackbar'],
                            });
                        },
                    );
                }
            });
    }

    mergeTags(tags: Tag[]) {
        this.matDialog
            .open(MergeTagDialogComponent, {
                data: tags,
                width: '60vw',
            })
            .afterClosed()
            .subscribe((result) => {
                if (result) {
                    this.selection.clear();
                    this.tagService.merge(result[0], result.slice(1)).subscribe(
                        () => {
                            this.form.updateValueAndValidity({
                                onlySelf: false,
                                emitEvent: true,
                            });
                        },
                        (err) => {
                            this.snackBar.open(err.error.detail, null, {
                                duration: 2000,
                                panelClass: ['error-snackbar'],
                            });
                        },
                    );
                }
            });
    }
}
